import type { AdminPaymentCancel } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { PAYMENT_CANCEL_STATUS_LABELS } from '@/entities/payment';
import { formatDateTime, formatWon } from '@/shared/lib';
import { Button, ContentCard, cn, StatusBadge, tableStyles } from '@/shared/ui';
import { PAYMENT_CANCEL_STATUS_TONES } from './payment-status-tones';

interface PaymentCancelsTableProps {
  readonly cancels: readonly AdminPaymentCancel[];
  readonly isRetrying: boolean;
  readonly onRetry: (cancelId: string) => void;
}

/** 환불 이력 — 처리 중(PENDING) 행에는 같은 멱등키로 다시 부르는 "다시 시도"가 있다(결제 설계 §5). */
export const PaymentCancelsTable = ({
  cancels,
  isRetrying,
  onRetry,
}: PaymentCancelsTableProps): ReactElement => (
  <ContentCard title="환불 이력">
    {cancels.length === 0 ? (
      <p className="text-sm text-muted-foreground">환불 이력이 없습니다.</p>
    ) : (
      <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
        <table className={tableStyles.table}>
          <thead className={tableStyles.head}>
            <tr>
              <th className={tableStyles.th}>요청 일시</th>
              <th className={cn(tableStyles.th, 'text-right')}>금액</th>
              <th className={tableStyles.th}>사유</th>
              <th className={tableStyles.th}>상태</th>
              <th className={tableStyles.th}>요청 운영자</th>
              <th className={tableStyles.th}>
                <span className="sr-only">처리</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {cancels.map((cancel) => {
              const status = PAYMENT_CANCEL_STATUS_LABELS[cancel.status];

              return (
                <tr key={cancel.id} className={tableStyles.row}>
                  <td className={cn(tableStyles.td, 'whitespace-nowrap')}>
                    {formatDateTime(cancel.createdAt)}
                  </td>
                  <td className={cn(tableStyles.td, 'text-right tabular-nums')}>
                    {formatWon(cancel.amount)}
                  </td>
                  <td className={cn(tableStyles.td, 'wrap-anywhere')}>{cancel.reason}</td>
                  <td className={tableStyles.td}>
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <StatusBadge tone={PAYMENT_CANCEL_STATUS_TONES[cancel.status]}>
                        {status.label}
                      </StatusBadge>
                      {cancel.failureMessage ? (
                        <span className="text-xs text-destructive">— {cancel.failureMessage}</span>
                      ) : null}
                    </span>
                  </td>
                  <td className={tableStyles.td}>{cancel.requestedByEmail ?? '상점관리자'}</td>
                  <td className={cn(tableStyles.td, 'text-right')}>
                    {cancel.status === 'PENDING' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        isLoading={isRetrying}
                        onClick={() => onRetry(cancel.id)}
                      >
                        다시 시도
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </ContentCard>
);
